import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createTextVNode(": ")
const _hoisted_2 = { key: 1 }

import { computed, ref, watchEffect } from 'vue';
import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { Token } from '@/sdk/entities/token';
import { TRANSACTION_FEE_NATIVE } from '@/constants/TRANSACTION_FEE_NATIVE';
import { fromWei, toWei } from '@/sdk/utils';
import { Fields } from '@/store/modules/swap/swap.module';
import { SWAP_FORM_INPUTS } from '@/store/modules/swap/constants/SWAP_FORM_INPUTS';
import { normalizeWeiByMinDecimalsWhenExistCardanoToken } from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import InputBalance from '@/components/form/InputBalance.vue';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useSwap } from '@/store/modules/swap/useSwap';
import { useSwapInfo } from '@/store/modules/swap/useSwapInfo';
import { useSwapMilkomedaWSCBridge } from '@/store/modules/swap/useSwapMilkomedaWSCBridge';
import { useMilkomedaWSCBalanceADAIntoMilkomedaValidator } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBalanceADAIntoMilkomedaValidator';
import { BIG_ZERO, min } from '@/utils/bigNumber';


export default /*#__PURE__*/_defineComponent({
  __name: 'SwapInputBalance',
  props: {
    token: null,
    field: null
  },
  emits: ["balanceError"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'balanceError', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  token: Token;
  field: Fields;
}

const { isPresentTokenIntoNetwork } = useTokens();
const { walletState } = useEVMWallet();
const { getBalanceByToken, balanceByTokenSymbolAndChainId } = useBalances();
const { swapForm, setAmount } = useSwap();
const { calculateWSCTotalFeesAndLookUpsWhenMaxADA } = useSwapInfo();
const {
  milkomedaWSCBridgeState,
  calculateMaxADAIntoCardanoAndMilkomeda,
  estimateMaxBridgeAmountFromCardano,
} = useSwapMilkomedaWSCBridge();
const { calculateMaxADAIntoMilkomedaWhenUnwrapADA } =
  useMilkomedaWSCBalanceADAIntoMilkomedaValidator();



const { t } = useI18n();

const maxAmount = ref(BIG_ZERO);
const isCalculateMaxAmount = ref(false);

const isSwapSameTokensFromMilkomedaToCardano = computed(
  () => swapForm.isSwapSameTokensFromMilkomedaToCardano,
);
const isBalanceTooltip = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) return false;
  return (
    (props.field === Fields.InputA &&
      isPresentTokenIntoNetwork(props.token.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)) ||
    isSwapSameTokensFromMilkomedaToCardano.value
  );
});

const isInsufficiencyADABalanceError = computed(
  () => !milkomedaWSCBridgeState.isValidCardanoBalance && !!milkomedaWSCBridgeState.minADABalance,
);
const isTokenWithFee = computed(() => props.token?.isETHToken());
const hasMaxBtn = computed(() => props.field === Fields.InputA);

const getBalance = () => {
  return getBalanceByToken(props.token)?.balance.toExact();
};
const balanceInCardano = computed(() => {
  const symbol = props.token.symbol!;
  const rawBalance = balanceByTokenSymbolAndChainId(symbol, +DEFAULT_CARDANO_CHAIN_ID!).value
    ?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});
const balanceInCardanoFixed = computed(() => balanceInCardano.value.toFixed(2));
const balanceInMilkomeda = computed(() => {
  const symbol = props.token.symbol!;
  const rawBalance = balanceByTokenSymbolAndChainId(symbol, +DEFAULT_NETWORK_ID!).value?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});
const balanceInMilkomedaFixed = computed(() => balanceInMilkomeda.value.toFixed(2));
const balance = computed(() => {
  if (isSwapSameTokensFromMilkomedaToCardano.value) {
    return props.field === Fields.InputA ? balanceInMilkomeda.value : balanceInCardano.value;
  }

  return new BigNumber(getBalance() ?? 0);
});

const isBalanceCanBeWithdrawn = computed(
  () =>
    (isTokenWithFee.value && balance.value.isGreaterThan(TRANSACTION_FEE_NATIVE)) ||
    (!isTokenWithFee.value && balance.value.isGreaterThan(0)),
);
const walletChainId = computed(() => walletState.network?.chainId ?? -1);

const canShowBalance = computed(() => {
  const tokenFromWalletChain = props.token ? +props.token?.chainId === +walletChainId.value : true;
  return (
    tokenFromWalletChain &&
    props.token &&
    getBalanceByToken(props.token)?.status === 'success' &&
    walletState.isInjected
  );
});
const isBalanceError = computed(() => {
  let isError = false;
  const amount = swapForm[props.field]?.amount ?? '0';

  if (props.field === Fields.InputA) {
    isError = maxAmount.value.isLessThan(amount);

    isError = isError || isInsufficiencyADABalanceError.value;
  }
  emits('balanceError', isError);
  return isError;
});

function setMax() {
  setAmount(maxAmount.value.toFixed(), props.field as unknown as SWAP_FORM_INPUTS);
}

async function calculateMaxAmount() {
  if (!walletState.isInjected) {
    return BIG_ZERO;
  }

  let maxBalance = balance.value;

  // NOTE:
  // When swap not same tokens and WSC enabled
  if (
    !isTokenWithFee.value &&
    ENABLE_FAKE_CARDANO_NETWORK &&
    !isSwapSameTokensFromMilkomedaToCardano.value
  ) {
    const estimatedMaxBridgeAmount = await estimateMaxBridgeAmountFromCardano(props.token);
    if (estimatedMaxBridgeAmount) {
      maxBalance = min(estimatedMaxBridgeAmount.plus(balanceInMilkomeda.value), maxBalance);
    }
  }

  // NOTE:
  // When ETH and WSC disabled
  if (isTokenWithFee.value && !ENABLE_FAKE_CARDANO_NETWORK) {
    maxBalance = balance.value.minus(TRANSACTION_FEE_NATIVE);
  }
  // NOTE:
  // WSC Swap ADA
  if (
    isTokenWithFee.value &&
    ENABLE_FAKE_CARDANO_NETWORK &&
    !isSwapSameTokensFromMilkomedaToCardano.value
  ) {
    const totalFeesAndLookUps = calculateWSCTotalFeesAndLookUpsWhenMaxADA();
    maxBalance = await calculateMaxADAIntoCardanoAndMilkomeda(totalFeesAndLookUps);
  }
  // NOTE:
  // WSC Unwrap all ADA from Milkomeda to Cardano
  if (
    isTokenWithFee.value &&
    ENABLE_FAKE_CARDANO_NETWORK &&
    isSwapSameTokensFromMilkomedaToCardano.value
  ) {
    maxBalance = calculateMaxADAIntoMilkomedaWhenUnwrapADA();
  }

  // NOTE:
  // When token
  const maxBalanceByMinDecimalsInWei = normalizeWeiByMinDecimalsWhenExistCardanoToken(
    toWei(maxBalance, props.token.decimals),
    props.token,
  );

  return fromWei(maxBalanceByMinDecimalsInWei, props.token.decimals);
}

async function updateMaxAmount() {
  if (!hasMaxBtn.value) return;

  const prevToken = props.token.address;
  isCalculateMaxAmount.value = true;
  const maxAmountValue = await calculateMaxAmount();
  isCalculateMaxAmount.value = false;
  // NOTE:
  // If token changed while we calculate max amount, then skip value and wait correct state.
  if (prevToken === props.token.address) {
    maxAmount.value = maxAmountValue;
  }
}

// Update max amount
// NOTE:
// `calculateMaxAmount` use different refs and we want to watch when they changed.
watchEffect(async () => {
  await updateMaxAmount();
});

return (_ctx: any,_cache: any) => {
  return (_unref(canShowBalance))
    ? (_openBlock(), _createBlock(InputBalance, {
        key: 0,
        "balance-value": _unref(balance),
        "is-balance-error": _unref(isBalanceError),
        onSetMax: setMax,
        "is-max-disabled": !_unref(isBalanceCanBeWithdrawn) || isCalculateMaxAmount.value,
        "not-max-button": !_unref(hasMaxBtn)
      }, _createSlots({
        default: _withCtx(() => [
          _hoisted_1
        ]),
        _: 2
      }, [
        (_unref(isBalanceTooltip))
          ? {
              name: "tooltip",
              fn: _withCtx(() => [
                (!_unref(isSwapSameTokensFromMilkomedaToCardano))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("div", null, [
                        _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Cardano' })) + ": ", 1),
                        _createElementVNode("span", null, _toDisplayString(_unref(balanceInCardanoFixed)), 1)
                      ]),
                      _createElementVNode("div", null, [
                        _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Milkomeda' })) + ": ", 1),
                        _createElementVNode("span", null, _toDisplayString(_unref(balanceInMilkomedaFixed)), 1)
                      ])
                    ], 64))
                  : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.tokensAvailable', {
              chain: __props.field === _unref(Fields).InputA ? 'Milkomeda' : 'Cardano',
            })) + ": ", 1),
                      _createElementVNode("span", null, _toDisplayString(__props.field === _unref(Fields).InputA ? _unref(balanceInMilkomedaFixed) : _unref(balanceInCardanoFixed)), 1)
                    ]))
              ])
            }
          : undefined
      ]), 1032, ["balance-value", "is-balance-error", "is-max-disabled", "not-max-button"]))
    : _createCommentVNode("", true)
}
}

})